import React, { useState } from 'react';
import {
  Card,
  TabList,
  Tabs,
} from 'ui-component-library';
import { func, string } from 'prop-types';

import StopSection from './shipment-stop-section';
import {
  BADGE_TYPES, STOP_TYPES, TOOLTIP_MESSAGES, MODE_TYPES,
} from '../../../../constants';
import Expand from '../card-shared-components/card-expand';
import Badge from '../badge';
import { CustomTab, LateBadgeContainer } from './card-shipment-stops.style';
import shipmentInformationShape from '../../../../shapes/shipment-information';
import theme from '../../../../theme';
import InfoTooltipButton from '../info-tooltip-button/info-tooltip-button';
import TerminalInfo from '../../ltl/terminal-info-section';

/**
 * The shipment stops card component
 * @param {object} params - Object containing props
 * @param {object} data - Data to show
 * @param {string} params.salesOrderBk - Sales order BK associated to this data
 * @param {string} params.status - Status associated to this data
 * @param {func} params.refetchData - Handles refetched relavent data
 * @return {element} - The Shipment Stops card component
 */
const ShipmentStops = ({
  data,
  loadId,
  salesOrderBk,
  status,
  refetchData,
  mode,
}) => {
  const [showInBetween, setShowInBetween] = useState(false);
  const [showLocationRemarks, setShowLocationRemarks] = useState(false);

  /**
   * Returns the total number of stops for a shipment
   * @return {number}
   */
  const getStopCount = () => data.inBetweenStops?.length + 2;

  /**
   * Checks to see if a user can edit a particular stop.
   * @param {string} locationNumber - Location number of stop
   * @return {boolean}
   */
  const canUpdateStop = (locationNumber) => {
    const stops = [
      data?.origin,
      ...(data.inBetweenStops ? data.inBetweenStops : []),
      data?.destination,
    ];

    for (let i = 1; i < stops.length; i += 1) {
      if (stops[i]?.locationNumber === locationNumber) {
        const previous = stops[i - 1];
        return (!!previous?.actualCheckIn && !!previous?.actualCheckOut);
      }
    }
    return false;
  };

  /**
   * Helper function to determine if any inBetween stops are late
   * @return {boolean}
   */
  const inBetweenStopLate = () => data.inBetweenStops?.filter((x) => x.isLate === true).length > 0;

  /**
   * Handles showing the location remarks
   * @return {void}
   */
  const handleShowLocationRemarks = () => setShowLocationRemarks(true);

  /**
   * Handles hiding the location remarks
   * @return {void}
   */
  const handleHideLocationRemarks = () => setShowLocationRemarks(false);

  /**
   * Conditionally renderes expand based on if there are
   * in between stops
   * @return {element | null}
   */
  const renderExpand = () => {
    if (!data.inBetweenStops || data.inBetweenStops.length === 0) {
      return null;
    }
    const expandMessage = showInBetween ? `Hide ${data.inBetweenStops.length} Stops` : `See all ${getStopCount()} Stops`;
    return (
      <Expand
        onClick={() => setShowInBetween(!showInBetween)}
        expandMessage={expandMessage}
        expanded={showInBetween}
        testId="expand"
      >
        {
          (inBetweenStopLate() && !showLocationRemarks)
          && <LateBadgeContainer><Badge type={BADGE_TYPES.late} /></LateBadgeContainer>
        }
      </Expand>
    );
  };

  return (
    <Card
      title="Shipment Stops Information"
      headerContent={<InfoTooltipButton text={TOOLTIP_MESSAGES['shipment-stop-info']} isPrimary />}
      isPaddingRemoved
    >
      <Tabs>
        <TabList
          width="50%"
          fontSize={theme.typography.sizes.small}
          $upperCase
        >
          <CustomTab onClick={handleHideLocationRemarks}>Primary Information</CustomTab>
          <CustomTab onClick={handleShowLocationRemarks}>Location Notes</CustomTab>
        </TabList>
      </Tabs>
      <StopSection
        stop={data.origin}
        testId={data.origin?.address?.address1}
        stopType={STOP_TYPES.origin}
        locationRemarksShown={showLocationRemarks}
        loadId={loadId}
        status={status}
        destinationLocationNumber={data.destination?.locationNumber}
        salesOrderBk={salesOrderBk}
        refetchData={refetchData}
        canEdit={mode === MODE_TYPES.truckload}
        mode={mode}
      />
      {renderExpand()}
      {
        showInBetween && data.inBetweenStops.map((stop, i) => (
          <StopSection
            key={i}
            stop={stop}
            testId={stop.address?.address1}
            stopType={STOP_TYPES.inBetween}
            locationRemarksShown={showLocationRemarks}
            loadId={loadId}
            status={status}
            destinationLocationNumber={data.destination?.locationNumber}
            salesOrderBk={salesOrderBk}
            refetchData={refetchData}
            canEdit={mode === MODE_TYPES.truckload && canUpdateStop(stop.locationNumber)}
            mode={mode}
          />
        ))
      }
      <StopSection
        stop={data.destination}
        testId={data.destination?.address?.address1}
        stopType={STOP_TYPES.destination}
        locationRemarksShown={showLocationRemarks}
        loadId={loadId}
        status={status}
        destinationLocationNumber={data.destination?.locationNumber}
        salesOrderBk={salesOrderBk}
        refetchData={refetchData}
        canEdit={mode === MODE_TYPES.truckload && canUpdateStop(data.destination?.locationNumber)}
        mode={mode}
      />
      {mode === MODE_TYPES.lessThanTruckload && (<TerminalInfo data={data} />)}
    </Card>
  );
};

ShipmentStops.propTypes = {
  data: shipmentInformationShape,
  loadId: string.isRequired,
  status: string.isRequired,
  salesOrderBk: string.isRequired,
  refetchData: func,
  mode: string.isRequired,
};

ShipmentStops.defaultProps = {
  data: {},
  refetchData: () => {},
};

export default ShipmentStops;
